/* Closena 2026 — Core JS */
'use strict';

/* ── Cart state ─────────────────────────────────── */
/* `closena:cart:updated` always carries the fresh cart in `event.detail`, so
   listeners never have to re-fetch /cart.js for themselves. */
function emitCartUpdate(cart) {
  document.dispatchEvent(new CustomEvent('closena:cart:updated', { detail: cart }));
}

const ClosenaCart = {
  async get() {
    const r = await fetch('/cart.js');
    return r.json();
  },

  async add(variantId, quantity = 1, properties = {}) {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity, properties })
    });
    if (!r.ok) throw new Error('Add to cart failed');
    const data = await r.json();
    /* /cart/add.js returns the line item, not the cart. */
    emitCartUpdate(await this.get());
    return data;
  },

  async update(updates) {
    const r = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates })
    });
    const cart = await r.json();
    emitCartUpdate(cart);
    return cart;
  },

  async remove(lineItemKey) {
    return this.update({ [lineItemKey]: 0 });
  },

  /* Applies a code in place. The /discount/CODE link is a full navigation — it
     dumped shoppers back on whatever page they were browsing and closed the
     drawer. Posting the code to /cart/update.js applies it to the cart with no
     page load, and switching codes works (it did not reliably via the link).
     An ineligible or unknown code returns 200 and leaves the cart untouched,
     which the rail reports as "Not applied to this cart". */
  async applyDiscount(code) {
    const r = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: code })
    });
    if (!r.ok) throw new Error('Apply discount failed');
    const cart = await r.json();
    emitCartUpdate(cart);
    return cart;
  },

  /* Shopify publishes no "remove discount code" endpoint. Posting an empty
     `discount` to /cart/update.js does clear it from the cart — but it leaves
     any discount_code cookie behind, which the offers rail would then read as
     "requested but not applied". So the cookie has to be expired by hand. */
  async removeDiscount() {
    try {
      document.cookie = 'discount_code=; Max-Age=0; path=/';
    } catch {}

    const r = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: '' })
    });
    const cart = await r.json();
    emitCartUpdate(cart);
    return cart;
  },

  /* Notes are saved silently — firing a cart update here would re-render the
     cart section out from under the textarea the shopper is typing in. */
  async setNote(note) {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note })
    });
  }
};

/* ── Applied-discount notes ─────────────────────────
   Keeps [data-discount-note] blocks honest after an Ajax cart change. Liquid
   renders their initial state; both read the discounts actually on the cart,
   never the discount_code cookie (which only records the last code requested,
   even when Shopify declines it). */
function appliedDiscountTitles(cart) {
  const titles = {};
  const add = d => { if (d && d.title) titles[String(d.title).toUpperCase()] = true; };
  (cart.items || []).forEach(i => (i.discounts || []).forEach(add));
  (cart.cart_level_discount_applications || []).forEach(add);
  return titles;
}

/* In the drawer the note competes with the item list for height, so it shows
   briefly and then gets out of the way — once hidden, the list is `flex: 1 1
   auto` and simply reclaims the space. On the cart page there is room, so it
   stays put. */
const NOTE_VISIBLE_MS = 5000;
const noteTimers = new WeakMap();

function flashNote(el) {
  clearTimeout(noteTimers.get(el));
  el.classList.remove('is-fading');
  el.hidden = false;

  noteTimers.set(el, setTimeout(() => {
    el.classList.add('is-fading');
    noteTimers.set(el, setTimeout(() => {
      el.hidden = true;
      el.classList.remove('is-fading');
    }, 300));
  }, NOTE_VISIBLE_MS));
}

function showNote(el) {
  if (el.closest('#cart-drawer')) flashNote(el);
  else el.hidden = false;
}

function updateDiscountNotes(cart) {
  const titles = appliedDiscountTitles(cart);
  document.querySelectorAll('[data-discount-note]').forEach(el => {
    const active = titles[el.getAttribute('data-discount-note')] === true;
    el.dataset.noteActive = active ? '1' : '';
    if (!active) {
      clearTimeout(noteTimers.get(el));
      el.classList.remove('is-fading');
      el.hidden = true;
      return;
    }
    showNote(el);
  });
}

/* ── Delivery estimate ──────────────────────────────
   Liquid can't price shipping, so the row renders "Calculated at checkout" and
   this swaps in the store's real rate. Failures leave the fallback in place. */
async function updateDeliveryEstimate() {
  const el = document.querySelector('[data-delivery-value]');
  const country = el && el.dataset.shippingCountry;
  if (!country) return;

  try {
    const url = `/cart/shipping_rates.json?shipping_address[country]=${encodeURIComponent(country)}`;
    const r = await fetch(url);
    if (!r.ok) return;

    const rates = (await r.json()).shipping_rates;
    if (!Array.isArray(rates) || rates.length === 0) return;

    const cheapest = rates.reduce((a, b) => (parseFloat(b.price) < parseFloat(a.price) ? b : a));
    const cents = Math.round(parseFloat(cheapest.price) * 100);

    el.innerHTML = cents === 0
      ? '<span class="fw-700 cart-summary__free">FREE</span>'
      : `<span class="fw-600">${cheapest.name} — ${formatMoney(cents)}</span>`;
  } catch {
    /* Keep whatever Liquid rendered. */
  }
}

/* ── Free-delivery progress ─────────────────────────
   Updates every instance on the page — the drawer's and the cart page's. */
function updateShippingProgress(cart) {
  const threshold = parseInt(document.body.dataset.freeShipping || 1500, 10) * 100;
  if (!threshold) return;

  const total = cart.total_price;
  const done = total >= threshold;
  const pct = Math.min(100, Math.round((total / threshold) * 100));

  document.querySelectorAll('[data-shipping-progress]').forEach(wrap => {
    /* The cart section re-renders itself from Liquid; touching its bar here
       would just flash a JS-formatted value before the swap lands. */
    if (wrap.closest('[data-cart-root]')) return;

    const bar = wrap.querySelector('[data-progress-bar]');
    const msg = wrap.querySelector('[data-progress-message]');

    if (bar) {
      /* A full bar says nothing — drop it and let the message stand alone. */
      bar.hidden = done;
      bar.style.setProperty('--progress', pct + '%');
      bar.setAttribute('aria-valuenow', pct);
    }
    wrap.classList.toggle('is-complete', done);
    if (msg) {
      if (done) {
        /* Shopify reports 0 once free shipping applies, so it can't tell us what
           the fee would have been — the saving comes from the theme setting. */
        const fee = parseInt(document.body.dataset.standardShipping || 0, 10) * 100;
        const saved = fee > 0 ? ` <span class="ship-progress__saved">Save ${formatMoney(fee)}</span>` : '';
        msg.innerHTML = `🎉 You qualify for free delivery!${saved}`;
      } else {
        msg.innerHTML = `🚚 Add <strong>${formatMoney(threshold - total)}</strong> more for free delivery`;
      }
    }
  });
}

/* ── Cart page ──────────────────────────────────────
   Re-renders the main-cart section so savings, discounts and the progress bar
   stay Liquid's job, not JS's.

   This refetches the whole /cart page rather than using the Section Rendering
   API (`?section_id=main-cart`). That API renders the section from its schema
   DEFAULTS — it ignores templates/cart.json, so the trust points and review
   blocks come back empty and the section settings revert. Verified against the
   live storefront, not just the local dev server. The full page costs a few
   extra KB and is unambiguously correct. */
const CartPage = {
  get host() {
    /* Not Shopify's wrapper id — in a JSON template that is generated per theme
       (shopify-section-template--<id>__main-cart), so it can't be hardcoded. */
    return document.querySelector('[data-cart-root]');
  },

  async refresh() {
    const host = this.host;
    if (!host) return;

    /* Preserve an order note the shopper has typed but not yet saved. */
    const pendingNote = host.querySelector('[data-cart-note]')?.value;

    try {
      const r = await fetch(window.location.pathname, { headers: { 'Accept': 'text/html' } });
      if (!r.ok) throw new Error('Cart page fetch failed');
      const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
      const fresh = doc.querySelector('[data-cart-root]');
      if (!fresh) throw new Error('Cart markup missing');
      host.innerHTML = fresh.innerHTML;
    } catch {
      window.location.reload();
      return;
    }

    const note = host.querySelector('[data-cart-note]');
    if (note && pendingNote != null) note.value = pendingNote;
  }
};

/* ── Cart drawer ────────────────────────────────── */
const CartDrawer = {
  el: null,
  overlay: null,

  init() {
    this.el = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    if (!this.el) return;

    document.querySelectorAll('[data-open-cart]').forEach(btn => {
      btn.addEventListener('click', e => { e.preventDefault(); this.open(); });
    });

    this.overlay?.addEventListener('click', () => this.close());

    document.getElementById('cart-drawer-close')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.el.classList.contains('is-open')) this.close();
    });

    /* Cart-wide refresh is wired at module level, not here — see below. */
  },

  open() {
    this.el.classList.add('is-open');
    this.overlay?.classList.add('is-visible');
    document.body.classList.add('drawer-open');
    this.el.querySelector('[data-cart-drawer-inner]')?.focus();

    /* Re-flash on open, or a note that timed out earlier in the page's life
       would never be seen. Liquid marks which ones apply on first render. */
    this.el.querySelectorAll('[data-discount-note]').forEach(el => {
      if (el.dataset.noteActive === '1' || !el.hidden) flashNote(el);
    });

    this.refresh();
  },

  close() {
    this.el.classList.remove('is-open');
    this.overlay?.classList.remove('is-visible');
    document.body.classList.remove('drawer-open');
  },

  async refresh(cart) {
    if (!this.el) return;
    const data = cart || await ClosenaCart.get();
    this.renderItems(data);
    this.renderSubtotal(data);
    updateShippingProgress(data);
  },

  renderSubtotal(cart) {
    /* Sits on the checkout button, so it matches Liquid's short format and
       clears itself when there is nothing to check out with. */
    const el = document.getElementById('cart-subtotal');
    if (el) el.textContent = cart.item_count > 0 ? formatMoneyShort(cart.total_price) : '';
    document.querySelectorAll('.cart-count').forEach(badge => {
      if (cart.item_count > 0) {
        badge.textContent = cart.item_count;
        badge.style.display = '';
      } else {
        badge.style.display = 'none';
      }
    });
  },

  renderItems(cart) {
    const container = this.el.querySelector('[data-cart-items]');
    if (!container) return;

    if (cart.item_count === 0) {
      container.innerHTML = `<p class="cart-drawer__empty">Your cart is empty.</p>`;
      return;
    }

    container.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}">
        <a href="${item.url}" class="cart-item__image-wrap">
          <img src="${item.image ? item.image.replace(/\.(jpg|jpeg|png|webp|gif)(\?|$)/i, '_80x80.$1$2') : ''}" alt="${item.product_title}" width="80" height="80" loading="lazy">
        </a>
        <div class="cart-item__body">
          <a href="${item.url}" class="cart-item__title">${item.product_title}</a>
          <p class="cart-item__variant text-secondary">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</p>
          <div class="cart-item__footer">
            <div class="cart-item__qty">
              <button class="qty-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
              <span class="qty-display">${item.quantity}</span>
              <button class="qty-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
            </div>
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
          </div>
        </div>
        <button class="cart-item__remove" data-remove="${item.key}" aria-label="Remove ${item.product_title}">×</button>
      </div>
    `).join('');
  },

};

/* ── Cart-wide refresh ──────────────────────────────
   One listener drives every surface that shows cart state. */
document.addEventListener('closena:cart:updated', e => {
  const cart = e.detail;
  if (cart) {
    updateShippingProgress(cart);
    updateDiscountNotes(cart);
  }
  CartDrawer.refresh(cart);
  CartPage.refresh().then(updateDeliveryEstimate);
});

/* ── Add to cart forms ──────────────────────────── */
document.addEventListener('submit', async e => {
  if (!e.target.matches('[data-atc-form]')) return;
  e.preventDefault();

  const form = e.target;
  const btn = form.querySelector('[type="submit"]');
  const variantId = form.querySelector('[name="id"]')?.value;
  if (!variantId) return;

  btn.disabled = true;
  btn.textContent = btn.dataset.loadingText || 'Adding…';

  try {
    await ClosenaCart.add(variantId);
    CartDrawer.open();
  } catch {
    btn.textContent = 'Error — try again';
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = btn.dataset.originalText || 'Add to Cart';
    }, 2000);
    return;
  }

  btn.disabled = false;
  btn.textContent = btn.dataset.originalText || 'Add to Cart';
});

/* ── Cart quantity / remove controls ─────────────────
   Works for both the cart drawer and the /cart page; both are re-rendered by
   the `closena:cart:updated` listener above, so no page reload is needed. */
document.addEventListener('click', async e => {
  const qtyBtn = e.target.closest('[data-action="increase"], [data-action="decrease"]');
  const removeBtn = e.target.closest('[data-remove]');
  if (!qtyBtn && !removeBtn) return;

  const list = (qtyBtn || removeBtn).closest('.cart-page__items');
  list?.classList.add('is-updating');

  try {
    if (qtyBtn) {
      const itemEl = qtyBtn.closest('.cart-item');
      const currentQty = parseInt(itemEl.querySelector('.qty-display').textContent, 10);
      const newQty = qtyBtn.dataset.action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
      await ClosenaCart.update({ [qtyBtn.dataset.key]: newQty });
    } else {
      await ClosenaCart.remove(removeBtn.dataset.remove);
    }
  } catch {
    window.location.reload();
  }
});

/* ── Order note ─────────────────────────────────────
   Delegated, because the cart section is swapped out on every cart change. */
let cartNoteTimer;
document.addEventListener('input', e => {
  const field = e.target.closest?.('[data-cart-note]');
  if (!field) return;

  clearTimeout(cartNoteTimer);
  cartNoteTimer = setTimeout(async () => {
    await ClosenaCart.setNote(field.value);
    const status = field.closest('.cart-note')?.querySelector('[data-cart-note-status]');
    if (!status) return;
    status.hidden = false;
    setTimeout(() => { status.hidden = true; }, 2000);
  }, 700);
});

/* ── Remove an applied discount ──────────────────── */
document.addEventListener('click', async e => {
  const btn = e.target.closest('[data-remove-discount]');
  if (!btn) return;
  e.preventDefault();
  btn.disabled = true;
  try {
    await ClosenaCart.removeDiscount();
  } catch {
    btn.disabled = false;
  }
});

/* ── Ticker ─────────────────────────────────────── */
function initTicker() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll('.ticker__track').forEach(track => {
    const clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    track.parentElement.appendChild(clone);
  });
}

/* ── Sticky header ──────────────────────────────── */
function initStickyHeader() {
  const header = document.getElementById('shopify-section-header');
  if (!header) return;
  const sentinel = document.getElementById('header-sentinel');

  const obs = new IntersectionObserver(([entry]) => {
    header.classList.toggle('header--scrolled', !entry.isIntersecting);
  }, { threshold: 0 });

  if (sentinel) obs.observe(sentinel);
}

/* ── Mobile menu ────────────────────────────────── */
function initMobileMenu() {
  const toggle = document.getElementById('mobile-menu-toggle');
  const menu = document.getElementById('mobile-nav');
  if (!toggle || !menu) return;

  toggle.addEventListener('click', () => {
    const open = menu.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', open);
    document.body.classList.toggle('mobile-nav-open', open);
  });
}

/* ── Mega menu ──────────────────────────────────── */
function initMegaMenu() {
  document.querySelectorAll('.nav-item--has-mega').forEach(item => {
    let closeTimer;
    const menu = item.querySelector('.mega-menu');
    if (!menu) return;

    item.addEventListener('mouseenter', () => {
      clearTimeout(closeTimer);
      menu.classList.add('is-visible');
    });
    item.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => menu.classList.remove('is-visible'), 150);
    });
    menu.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    menu.addEventListener('mouseleave', () => {
      closeTimer = setTimeout(() => menu.classList.remove('is-visible'), 150);
    });
  });
}

/* ── FAQ accordion ──────────────────────────────── */
function initAccordions() {
  document.querySelectorAll('.accordion__item').forEach(item => {
    const btn = item.querySelector('.accordion__trigger');
    const panel = item.querySelector('.accordion__panel');
    if (!btn || !panel) return;

    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', !open);
      panel.hidden = open;
    });
  });
}

/* ── Wishlist toggles (product cards) ──────────────── */
function initWishlistToggles() {
  const STORAGE_KEY = 'closena_wishlist';
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }
  function setWishlist(list) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }
  const wishlist = getWishlist();
  document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
    const id = btn.dataset.productId;
    if (wishlist.includes(id)) btn.setAttribute('aria-pressed', 'true');
    btn.addEventListener('click', e => {
      e.preventDefault();
      const list = getWishlist();
      const idx = list.indexOf(id);
      if (idx === -1) { list.push(id); btn.setAttribute('aria-pressed', 'true'); }
      else { list.splice(idx, 1); btn.setAttribute('aria-pressed', 'false'); }
      setWishlist(list);
    });
  });
}

/* ── Helpers ─────────────────────────────────────── */
/* Mirrors Liquid's `money` filter using the shop's own money_format, so a
   JS-updated price never changes shape when the same value is re-rendered
   server-side (e.g. "Rs.1,050.00" vs. "Rs 1,050"). */
function withDelimiters(cents, precision, thousands, decimal) {
  const [int, frac] = (cents / 100).toFixed(precision).split('.');
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  return frac ? grouped + decimal + frac : grouped;
}

function formatMoney(cents) {
  const format = document.body.dataset.moneyFormat || 'Rs.{{amount}}';

  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
    switch (placeholder) {
      case 'amount_no_decimals': return withDelimiters(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return withDelimiters(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return withDelimiters(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return withDelimiters(cents, 2, "'", '.');
      default: return withDelimiters(cents, 2, ',', '.');
    }
  });
}

/* Mirrors Liquid's money_without_trailing_zeros: drops a bare ".00" but keeps
   real paisa, so a rounded-looking total is never a wrong one. */
function formatMoneyShort(cents) {
  if (cents % 100 !== 0) return formatMoney(cents);

  const format = document.body.dataset.moneyFormat || 'Rs.{{amount}}';
  return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) =>
    placeholder.indexOf('comma_separator') !== -1
      ? withDelimiters(cents, 0, '.', ',')
      : withDelimiters(cents, 0, ',', '.')
  );
}

/* ── Boot ────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  CartDrawer.init();
  initTicker();
  initStickyHeader();
  initMobileMenu();
  initMegaMenu();
  initAccordions();
  initWishlistToggles();
  updateDeliveryEstimate();
});
